import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { CheckCircleOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { AbnormalPendingItem, c514Api } from '../../services/c514Api';
import {
  BatchAcceptanceBridge,
  pwc514f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';
import { useC514 } from './hooks/useC514';
import C514DetailDrawer, { detailColumns as c514DetailColumns } from './C514DetailDrawer';
import { useModulePermission } from '../../hooks/usePermission';
import './C514Approval.css';

const { Title, Text } = Typography;
const acceptanceActionIds = {
  list: 'PWC514F1-ACT-QUERY',
  approve: 'PWC514F1-ACT-APPROVE',
};
void acceptanceActionIds;

// ============================================================
// Helpers
// ============================================================

function renderAbnormalReason(code: string): React.ReactNode {
  if (code === 'P') return <Tag color="orange">單價異常</Tag>;
  if (code === 'Q') return <Tag color="blue">數量超交</Tag>;
  if (code === 'L') return <Tag color="red">單價異常及數量超交</Tag>;
  return <Tag color="default">{code || '—'}</Tag>;
}

function renderApprovalStatus(stat: string): React.ReactNode {
  if (stat === 'D') return <Tag color="orange">待核准</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  if (stat === 'N') return <Tag color="red">已駁回</Tag>;
  return <Tag color="default">{stat || '—'}</Tag>;
}

function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString('zh-TW');
  } catch {
    return date;
  }
}

// ============================================================
// Main Component
// ============================================================

const C514Approval: React.FC = () => {
  const acceptanceMode = useAcceptanceMode();
  const [detailSchemaPreview, setDetailSchemaPreview] = useState(false);
  const {
    loading,
    approving,
    pendingItems,
    total,
    selectedKeys,
    filter,
    detailData,
    detailLoading,
    detailVisible,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
    loadDetail,
    closeDetail,
  } = useC514();

  const permissions = useModulePermission('pwc514f1');

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<AbnormalPendingItem> = [
    {
      title: <span data-contract-field="fc120_01">交運單號</span>,
      dataIndex: 'do_no',
      key: 'do_no',
      width: 100,
      render: (text: string) => (
        <span
          className="c514-do-no-link"
          onClick={() => loadDetail(text)}
          title="點擊查看明細"
        >
          {text}
        </span>
      ),
    },
    {
      title: <span data-contract-field="fc120_11">交運日期</span>,
      dataIndex: 'do_date',
      key: 'do_date',
      width: 100,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: <span data-contract-field="fc120_12">客戶代號</span>,
      dataIndex: 'customer_code',
      key: 'customer_code',
      width: 90,
    },
    {
      title: <span data-contract-field="ui_customer_name">客戶名稱</span>,
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="ui_dept_name">部門</span>,
      dataIndex: 'dept_name',
      key: 'dept_name',
      width: 100,
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fc120_24">異常原因</span>,
      dataIndex: 'abnormal_reason',
      key: 'abnormal_reason',
      width: 160,
      render: (code: string) => renderAbnormalReason(code),
    },
    {
      title: <span data-contract-field="fc120_80">狀態</span>,
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 80,
      align: 'center',
      render: renderApprovalStatus,
    },
    {
      title: '操作',
      key: 'action',
      width: 70,
      align: 'center',
      render: (_: unknown, record: AbnormalPendingItem) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => loadDetail(record.do_no)}
        >
          明細
        </Button>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
    getCheckboxProps: (record: AbnormalPendingItem) => ({
      disabled: record.approval_status === 'P',
    }),
  };

  const handleSearch = () => { fetchPending(); };
  const handleFilterChange = (field: 'doStart' | 'doEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  return (
    <div className="c514-container">
      <Title level={4} style={{ marginBottom: 16 }}>異常交運單核准 (pwc514f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="query_do_start">交運單號 從</span>}>
            <Input
              value={filter.doStart}
              onChange={(e) => handleFilterChange('doStart', e.target.value)}
              placeholder="起始單號"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="query_do_end">至</span>}>
            <Input
              value={filter.doEnd}
              onChange={(e) => handleFilterChange('doEnd', e.target.value)}
              placeholder="結尾單號"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              data-acceptance-action="PWC514F1-ACT-QUERY"
              onClick={handleSearch}
              loading={loading}
              disabled={!permissions.canView}
            >
              確定範圍
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待核准
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              <Popconfirm
                title={`確認核准 ${selectedKeys.length} 筆異常交運單？`}
                description="核准後將觸發庫存、應收帳款及財務分錄更新，此操作無法撤銷。"
                onConfirm={batchApprove}
                okText="確認核准"
                cancelText="取消"
                disabled={selectedKeys.length === 0}
              >
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  data-acceptance-action="PWC514F1-ACT-APPROVE"
                  loading={approving}
                  disabled={selectedKeys.length === 0 || !permissions.canApprove}
                >
                  存檔（批量核准）
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<AbnormalPendingItem>
        rowKey="do_no"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (t) => `共 ${t} 筆` }}
        scroll={{ x: 900 }}
        locale={{ emptyText: '無待核准資料' }}
      />

      <C514DetailDrawer
        visible={detailVisible}
        loading={detailLoading}
        data={detailData}
        onClose={() => {
          setDetailSchemaPreview(false);
          closeDetail();
        }}
      />
      {acceptanceMode.isEnabled(pwc514f1AcceptanceManifest.formId) && (
        <>
          <label data-acceptance-non-field style={{ display: 'inline-flex', gap: 6, marginTop: 8 }}>
            <input
              type="checkbox"
              data-contract-field="ui_detail_selected"
              checked={detailSchemaPreview}
              onChange={event => setDetailSchemaPreview(event.target.checked)}
            />
            驗收：顯示明細欄位結構
          </label>
          {detailSchemaPreview && (
            <Table
              rowKey="fc121_02"
              columns={c514DetailColumns}
              dataSource={[]}
              size="small"
              pagination={false}
              style={{ marginTop: 8 }}
            />
          )}
        </>
      )}
      <BatchAcceptanceBridge
        manifest={pwc514f1AcceptanceManifest}
        records={pendingItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.do_no}
        queryExact={(key) => c514Api.getDetail(key)}
        lastApiResponse={{ items: pendingItems, total }}
        loading={loading || approving}
        onAutoFill={() => {
          const first = pendingItems[0];
          if (first) setSelectedKeys([first.do_no]);
        }}
        autoFillDisabled={pendingItems.length === 0}
        autoFillReason="先查詢一筆可核准的異常交運單，再由驗收模式勾選。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default C514Approval;
